import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { Colors } from "@miplatform/types";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";

export function ConfigAccordionHeader(
  props: Readonly<IConfigAccordionHeaderProps>
) {
  const {
    children,
    title,
    color = Colors.Primary,
    customAction,
    disableCollapse = false,
  } = props;
  const [expanded, setExpanded] = useState(true);
  const { classes, theme } = useStyles({ color, disableCollapse });
  const textColor =
    color === Colors.Primary
      ? theme.palette.common.white
      : theme.palette.primary.medium;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => {
        if (disableCollapse) return;
        setExpanded(isExpanded);
      }}
      disableGutters
      className={classes.accordion}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={
          !disableCollapse ? (
            <MiIcon color={textColor} path={mdiChevronDown} />
          ) : null
        }
      >
        <Box className={classes.headerContainer}>
          <Typography variant="HD4" color={textColor}>
            {title}
          </Typography>
          {customAction}
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
const useStyles = makeStyles<{
  color: "primary" | "secondary";
  disableCollapse: boolean;
}>()((theme, { color, disableCollapse }) => ({
  accordion: {
    borderRadius: 4,
    "&:before": {
      display: "none",
    },
    "& .MuiAccordionSummary-root.Mui-expanded": {
      transition: "none",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .MuiAccordionSummary-root": {
      cursor: disableCollapse ? "default !important" : "pointer",
    },
    "& .Mui-focusVisible": {
      backgroundColor:
        color === Colors.Secondary
          ? theme.palette.primary.light
          : theme.palette.primary.medium,
    },
  },
  accordionSummary: {
    transition: "border-radius 250ms ease-in-out",
    backgroundColor:
      color === Colors.Secondary
        ? theme.palette.primary.light
        : theme.palette.primary.medium,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0, 3),
    borderRadius: 4,
    minHeight: 42,
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  accordionDetails: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 3, 3),
    borderRadius: "0 0 4px 4px",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

interface IConfigAccordionHeaderProps {
  title: string;
  color?: "primary" | "secondary";
  children: React.ReactNode;
  customAction?: React.ReactNode;
  disableCollapse?: boolean;
}
